export interface CountryOption {
  stripeCode: string;
  name: string;
  entity: string;
  zipPattern?: RegExp;
}

export interface CustomerDetails {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip: string;
}

export interface BillingPayload {
  paymentMethodId: string;
  plan: string;
  billingPeriod: 'monthly' | 'yearly';
  currency: string;
  customerDetails: CustomerDetails;
}

export const CHARGEBEE_PLANS = {
  'starter': 'SS_Starter',
  'growth': 'SS_Growth',
  'plus': 'SS_Plus',
  'premium': 'SS_Premium',
} as const;

export const PLAN_MAPPING = {
  '1': 'SS_Starter',
  '2': 'SS_Growth',
  '3': 'SS_Plus',
  '4': 'SS_Premium'
} as const;

export const ORG_SIZE_MAPPING= {
  '1 - 75': '1',
  '76 - 150': '2',
  '151 - 300': '3',
  '301 - 500': '4'
} as const;


export const MAP_ORG_SIZE = {
  'SS_Starter': '1',
  'SS_Growth': '2',
  'SS_Plus': '3',
  'SS_Premium': '4'
} as const;

export const COUNTRY_MAPPING: CountryOption[] = [
  {stripeCode: 'AU', name: $localize`Australia`, entity: 'xref-au', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'NZ', name: $localize`New Zealand`, entity: 'xref-nz', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'US', name: $localize`United States`, entity: 'xref-usa', zipPattern: /^[0-9]{5}(-[0-9]{4})?$/},
  {stripeCode: 'CA', name: $localize`Canada`, entity: 'xref-can', zipPattern: /^[A-Za-z][0-9][A-Za-z] [0-9][A-Za-z][0-9]$/},
  {stripeCode: 'GB', name: $localize`United Kingdom`, entity: 'xref-uk', zipPattern: /^[A-Za-z]{1,2}[0-9][A-Za-z0-9]? [0-9][A-Za-z]{2}$/},
  {stripeCode: 'AE', name: $localize`United Arab Emirates`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'AQ', name: $localize`Antarctica`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'AG', name: $localize`Antigua and Barbuda`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'AF', name: $localize`Afghanistan`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AL', name: $localize`Albania`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AM', name: $localize`Armenia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AW', name: $localize`Aruba`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'AO', name: $localize`Angola`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AR', name: $localize`Argentina`, entity: 'xref-usa', zipPattern: /^[A-Za-z]{1}[0-9]{4}[A-Za-z]{3}$/},
  {stripeCode: 'AT', name: $localize`Austria`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AZ', name: $localize`Azerbaijan`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BA', name: $localize`Bosnia and Herzegovina`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'BD', name: $localize`Bangladesh`, entity: 'xref-au', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BE', name: $localize`Belgium`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BZ', name: $localize`Belize`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'BG', name: $localize`Bulgaria`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BF', name: $localize`Burkina Faso`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'BI', name: $localize`Burundi`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'BH', name: $localize`Bahrain`, entity: 'xref-uk', zipPattern: /^[0-9]{3,4}$/},
  {stripeCode: 'BJ', name: $localize`Benin`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'BM', name: $localize`Bermuda`, entity: 'xref-usa', zipPattern: /^[A-Z]{2} ?[A-Z0-9]{2}$/},
  {stripeCode: 'BN', name: $localize`Brunei`, entity: 'xref-au', zipPattern: /^[A-Za-z]{2}[0-9]{4}$/},
  {stripeCode: 'BO', name: $localize`Bolivia`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BS', name: $localize`Bahamas`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'BT', name: $localize`Bhutan`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'BW', name: $localize`Botswana`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IO', name: $localize`British Indian Ocean Territory`, entity: 'xref-usa', zipPattern: /^BBND 1ZZ$/},
  {stripeCode: 'VG', name: $localize`British Virgin Islands`, entity: 'xref-usa', zipPattern: /^VG[0-9]{4}$/},
  {stripeCode: 'CH', name: $localize`Switzerland`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'CI', name: $localize`Ivory Coast`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'CM', name: $localize`Cameroon`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CF', name: $localize`Central African Republic`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'TD', name: $localize`Chad`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CL', name: $localize`Chile`, entity: 'xref-usa', zipPattern: /^[0-9]{7}$/},
  {stripeCode: 'CX', name: $localize`Christmas Island`, entity: 'xref-usa', zipPattern: /^6798$/},
  {stripeCode: 'CC', name: $localize`Cocos Islands`, entity: 'xref-usa', zipPattern: /^6799$/},
  {stripeCode: 'CO', name: $localize`Colombia`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'KM', name: $localize`Comoros`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CK', name: $localize`Cook Islands`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CR', name: $localize`Costa Rica`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'CY', name: $localize`Cyprus`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'CU', name: $localize`Cuba`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'CW', name: $localize`Curacao`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CZ', name: $localize`Czech Republic`, entity: 'xref-uk', zipPattern: /^[0-9]{3} [0-9]{2}$/},
  {stripeCode: 'CD', name: $localize`Democratic Republic of the Congo`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'DE', name: $localize`Germany`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'DK', name: $localize`Denmark`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'DO', name: $localize`Dominican Republic`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'DZ', name: $localize`Algeria`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'TL', name: $localize`East Timor`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'EC', name: $localize`Ecuador`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'EE', name: $localize`Estonia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'EG', name: $localize`Egypt`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GQ', name: $localize`Equatorial Guinea`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'ER', name: $localize`Eritrea`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'ES', name: $localize`Spain`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'ET', name: $localize`Ethiopia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'FK', name: $localize`Falkland Islands`, entity: 'xref-usa', zipPattern: /^FIQQ 1ZZ$/},
  {stripeCode: 'FI', name: $localize`Finland`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'FJ', name: $localize`Fiji`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'FR', name: $localize`France`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GA', name: $localize`Gabon`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GH', name: $localize`Ghana`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GM', name: $localize`Gambia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GR', name: $localize`Greece`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GD', name: $localize`Grenada`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'GT', name: $localize`Guatemala`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GG', name: $localize`Guernsey`, entity: 'xref-usa', zipPattern: /^GY\d[\dA-Z]? ?\d[ABD-HJLN-UW-Z]{2}$/},
  {stripeCode: 'GW', name: $localize`Guinea-Bissau`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'GY', name: $localize`Guyana`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'HK', name: $localize`Hong Kong`, entity: 'xref-au', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'HR', name: $localize`Croatia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'HU', name: $localize`Hungary`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'ID', name: $localize`Indonesia`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IR', name: $localize`Iran`, entity: 'xref-usa', zipPattern: /^[0-9]{5}-?[0-9]{5}$/},
  {stripeCode: 'IQ', name: $localize`Iraq`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IE', name: $localize`Ireland`, entity: 'xref-uk', zipPattern: /^[A-Za-z0-9]{3} [A-Za-z0-9]{4}$/},
  {stripeCode: 'IM', name: $localize`Isle of Man`, entity: 'xref-usa', zipPattern: /^IM\d[\dA-Z]?\s?\d[ABD-HJLN-UW-Z]{2}$/},
  {stripeCode: 'IL', name: $localize`Israel`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IS', name: $localize`Iceland`, entity: 'xref-uk', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'IT', name: $localize`Italy`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'JM', name: $localize`Jamaica`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'JO', name: $localize`Jordan`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'JP', name: $localize`Japan`, entity: 'xref-au', zipPattern: /^[0-9]{3}-[0-9]{4}$/},
  {stripeCode: 'JE', name: $localize`Jersey`, entity: 'xref-usa', zipPattern: /^JE\d[\dA-Z]?\s?\d[ABD-HJLN-UW-Z]{2}$/},
  {stripeCode: 'KE', name: $localize`Kenya`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'KH', name: $localize`Cambodia`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'KR', name: $localize`South Korea`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'RS', name: $localize`Kosovo`, entity: 'xref-uk', zipPattern: /^\d{5,6}$/},
  {stripeCode: 'KW', name: $localize`Kuwait`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'KZ', name: $localize`Kazakhstan`, entity: 'xref-uk', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'LA', name: $localize`Laos`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'LC', name: $localize`Saint Lucia`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'LI', name: $localize`Liechtenstein`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'LK', name: $localize`Sri Lanka`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'LR', name: $localize`Liberia`, entity: 'xref-usa', zipPattern: /^\d{4}$/},
  {stripeCode: 'LY', name: $localize`Libya`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'LT', name: $localize`Lithuania`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'LU', name: $localize`Luxembourg`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'LV', name: $localize`Latvia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'MA', name: $localize`Morocco`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'MC', name: $localize`Monaco`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'MD', name: $localize`Moldova`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'MG', name: $localize`Madagascar`, entity: 'xref-uk', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'ML', name: $localize`Mali`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'MK', name: $localize`Macedonia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'MN', name: $localize`Mongolia`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'ME', name: $localize`Montenegro`, entity: 'xref-usa', zipPattern: /^8\d{4}$/},
  {stripeCode: 'MO', name: $localize`Macau`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'MT', name: $localize`Malta`, entity: 'xref-uk', zipPattern: /^[A-Za-z]{3} [0-9]{4}$/},
  {stripeCode: 'MR', name: $localize`Mauritania`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'MU', name: $localize`Mauritius`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'YT', name: $localize`Mayotte`, entity: 'xref-usa', zipPattern: /^976\d{2}$/},
  {stripeCode: 'MX', name: $localize`Mexico`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'MY', name: $localize`Malaysia`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'MZ', name: $localize`Mozambique`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'MM', name: $localize`Myanmar`, entity: 'xref-usa', zipPattern: /^\d{5}$/},
  {stripeCode: 'NA', name: $localize`Namibia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'NR', name: $localize`Nauru`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'NE', name: $localize`Niger`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'NG', name: $localize`Nigeria`, entity: 'xref-uk', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'NU', name: $localize`Niue`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'KP', name: $localize`North Korea`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'NL', name: $localize`Netherlands`, entity: 'xref-uk', zipPattern: /^[0-9]{4} [A-Za-z]{2}$/},
  {stripeCode: 'NO', name: $localize`Norway`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'OM', name: $localize`Oman`, entity: 'xref-uk', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'PA', name: $localize`Panama`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'PE', name: $localize`Peru`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'PH', name: $localize`Philippines`, entity: 'xref-au', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'PN', name: $localize`Pitcairn`, entity: 'xref-usa', zipPattern: /^PCRN 1ZZ$/},
  {stripeCode: 'PK', name: $localize`Pakistan`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'PS', name: $localize`Palestine`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'PL', name: $localize`Poland`, entity: 'xref-uk', zipPattern: /^[0-9]{2}-[0-9]{3}$/},
  {stripeCode: 'PT', name: $localize`Portugal`, entity: 'xref-uk', zipPattern: /^[0-9]{4}-[0-9]{3}$/},
  {stripeCode: 'PY', name: $localize`Paraguay`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'QA', name: $localize`Qatar`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'CD', name: $localize`Republic of the Congo`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'RO', name: $localize`Romania`, entity: 'xref-uk', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'RS', name: $localize`Serbia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'RW', name: $localize`Rwanda`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BL', name: $localize`Saint Barthelemy`, entity: 'xref-usa', zipPattern: /^9[78][01]\d{2}$/},
  {stripeCode: 'SH', name: $localize`Saint Helena`, entity: 'xref-usa', zipPattern: /^(ASCN|STHL) 1ZZ$/},
  {stripeCode: 'KN', name: $localize`Saint Kitts and Nevis`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SX', name: $localize`Saint Martin`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'PM', name: $localize`Saint Pierre and Miquelon`, entity: 'xref-usa', zipPattern: /^9[78]5\d{2}$/},
  {stripeCode: 'VC', name: $localize`Saint Vincent and the Grenadines`, entity: 'xref-usa', zipPattern: /^VC\d{4}$/},
  {stripeCode: 'WS', name: $localize`Samoa`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'ST', name: $localize`Sao Tome and Principe`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SA', name: $localize`Saudi Arabia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'SC', name: $localize`Seychelles`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SL', name: $localize`Sierra Leone`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SE', name: $localize`Sweden`, entity: 'xref-uk', zipPattern: /^[0-9]{3} [0-9]{2}$/},
  {stripeCode: 'SG', name: $localize`Singapore`, entity: 'xref-au', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'SX', name: $localize`Sint Maarten`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SI', name: $localize`Slovenia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'SK', name: $localize`Slovakia`, entity: 'xref-uk', zipPattern: /^[0-9]{3} [0-9]{2}$/},
  {stripeCode: 'SB', name: $localize`Solomon Islands`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SO', name: $localize`Somalia`, entity: 'xref-usa', zipPattern: /^[A-Z]{2} ?\d{5}$/},
  {stripeCode: 'SM', name: $localize`San Marino`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'SN', name: $localize`Senegal`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'SS', name: $localize`South Sudan`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SD', name: $localize`Sudan`, entity: 'xref-usa', zipPattern: /^\d{5}$/},
  {stripeCode: 'SR', name: $localize`Suriname`,entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SJ', name: $localize`Svalbard and Jan Mayen`, entity: 'xref-usa', zipPattern: /^\d{4}$/},
  {stripeCode: 'SV', name: $localize`El Salvador`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'TH', name: $localize`Thailand`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'TG', name: $localize`Togo`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'TK', name: $localize`Tokelau`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'TN', name: $localize`Tunisia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'TR', name: $localize`Turkey`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'TC', name: $localize`Turks and Caicos Islands`, entity: 'xref-usa', zipPattern: /^TKCA 1ZZ$/},
  {stripeCode: 'TT', name: $localize`Trinidad and Tobago`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'TW', name: $localize`Taiwan`, entity: 'xref-au', zipPattern: /^[0-9]{3,5}$/},
  {stripeCode: 'TZ', name: $localize`Tanzania`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'VI', name: $localize`U.S. Virgin Islands`, entity: 'xref-usa', zipPattern: /^008(?:[0-4]\d|5[01])(?:[ -]\d{4})?$/},
  {stripeCode: 'UY', name: $localize`Uruguay`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'UZ', name: $localize`Uzbekistan`, entity: 'xref-uk', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'VU', name: $localize`Vanuatu`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'VA', name: $localize`Vatican`, entity: 'xref-usa', zipPattern: /^00120$/},
  {stripeCode: 'VN', name: $localize`Vietnam`, entity: 'xref-au', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'WF', name: $localize`Wallis and Futuna`, entity: 'xref-usa', zipPattern: /^986\d{2}$/},
  {stripeCode: 'EH', name: $localize`Western Sahara`, entity: 'xref-usa', zipPattern: /^\d{5}$/},
  {stripeCode: 'ZA', name: $localize`South Africa`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AS', name: $localize`American Samoa`, entity: 'xref-usa', zipPattern: /^96799$/},
  {stripeCode: 'AD', name: $localize`Andorra`, entity: 'xref-usa', zipPattern: /^AD[0-9]{3}$/},
  {stripeCode: 'AI', name: $localize`Anguilla`, entity: 'xref-usa', zipPattern: /^AI-2640$/},
  {stripeCode: 'BB', name: $localize`Barbados`, entity: 'xref-usa', zipPattern: /^BB[0-9]{5}$/},
  {stripeCode: 'BY', name: $localize`Belarus`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'BR', name: $localize`Brazil`, entity: 'xref-usa', zipPattern: /^[0-9]{5}-?[0-9]{3}$/},
  {stripeCode: 'IC', name: $localize`Canary Islands`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'CV', name: $localize`Cape Verde`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'KY', name: $localize`Cayman Islands`, entity: 'xref-usa', zipPattern: /^KY[1-3]-[0-9]{4}$/},
  {stripeCode: 'CN', name: $localize`China`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'DJ', name: $localize`Djibouti`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'DM', name: $localize`Dominica`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'FO', name: $localize`Faroe Islands`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'GF', name: $localize`French Guiana`, entity: 'xref-usa', zipPattern: /^9[78]3[0-9]{2}$/},
  {stripeCode: 'PF', name: $localize`French Polynesia`, entity: 'xref-usa', zipPattern: /^987[0-9]{2}$/},
  {stripeCode: 'GE', name: $localize`Georgia`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'GI', name: $localize`Gibraltar`, entity: 'xref-usa', zipPattern: /^GX11 1AA$/},
  {stripeCode: 'GL', name: $localize`Greenland`, entity: 'xref-usa', zipPattern: /^39[0-9]{2}$/},
  {stripeCode: 'GP', name: $localize`Guadeloupe`, entity: 'xref-usa', zipPattern: /^9[78][01][0-9]{2}$/},
  {stripeCode: 'GU', name: $localize`Guam`, entity: 'xref-usa', zipPattern: /^969[123][0-9]([ -][0-9]{4})?$/},
  {stripeCode: 'GN', name: $localize`Guinea`, entity: 'xref-usa', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'HT', name: $localize`Haiti`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'HN', name: $localize`Honduras`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IN', name: $localize`India`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'IE', name: $localize`Ireland, Republic of`, entity: 'xref-usa', zipPattern: /^[A-Z][0-9]{2} ?[A-Z0-9]{4}$/},
  {stripeCode: 'KI', name: $localize`Kiribati`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{6}$/},
  {stripeCode: 'KG', name: $localize`Kyrgyzstan`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'LB', name: $localize`Lebanon`, entity: 'xref-usa', zipPattern: /^[0-9]{4}( ?[0-9]{4})?$/},
  {stripeCode: 'LS', name: $localize`Lesotho`, entity: 'xref-usa', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'MW', name: $localize`Malawi`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'MV', name: $localize`Maldives`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'MH', name: $localize`Marshall Islands`, entity: 'xref-usa', zipPattern: /^969[67][0-9]([ -][0-9]{4})?$/},
  {stripeCode: 'MQ', name: $localize`Martinique`, entity: 'xref-usa', zipPattern: /^9[78]2[0-9]{2}$/},
  {stripeCode: 'FM', name: $localize`Micronesia`, entity: 'xref-usa', zipPattern: /^9694[1-4]([ -][0-9]{4})?$/},
  {stripeCode: 'MS', name: $localize`Montserrat`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{7}$/},
  {stripeCode: 'NP', name: $localize`Nepal`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'AN', name: $localize`Netherlands Antilles`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{6}/},
  {stripeCode: 'NC', name: $localize`New Caledonia`, entity: 'xref-usa', zipPattern: /^988[0-9]{2}$/},
  {stripeCode: 'NI', name: $localize`Nicaragua`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'NF', name: $localize`Norfolk Island`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'MP', name: $localize`Northern Mariana Islands`, entity: 'xref-usa', zipPattern: /^9695[012]([ -][0-9]{4})?$/},
  {stripeCode: 'PW', name: $localize`Palau`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'PG', name: $localize`Papua New Guinea`, entity: 'xref-usa', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'PR', name: $localize`Puerto Rico`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'RE', name: $localize`Reunion`, entity: 'xref-usa', zipPattern: /^9[78]4[0-9]{2}$/},
  {stripeCode: 'RU', name: $localize`Russia`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'BL', name: $localize`St. Barthelemy`, entity: 'xref-usa', zipPattern: /^9[78][01][0-9]{2}$/},
  {stripeCode: 'KN', name: $localize`St. Kitts & Nevis`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{6}$/},
  {stripeCode: 'MF', name: $localize`St. Martin`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'VC', name: $localize`St. Vincent & the Grenadines`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{6}$/},
  {stripeCode: 'SZ', name: $localize`Swaziland`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{4}$/},
  {stripeCode: 'SY', name: $localize`Syria`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'TJ', name: $localize`Tajikistan`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'TO', name: $localize`Tonga`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'TT', name: $localize`Trinidad & Tobago`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'TM', name: $localize`Turkmenistan`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'TC', name: $localize`Turks & Caicos Islands`, entity: 'xref-usa', zipPattern: /^TKCA 1ZZ$/},
  {stripeCode: 'TV', name: $localize`Tuvalu`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'UG', name: $localize`Uganda`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'UA', name: $localize`Ukraine`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'VA', name: $localize`Vatican City`, entity: 'xref-usa', zipPattern: /^00120$/},
  {stripeCode: 'VE', name: $localize`Venezuela`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'VG', name: $localize`Virgin Islands, British`, entity: 'xref-usa', zipPattern: /^VG[0-9]{4}$/},
  {stripeCode: 'VI', name: $localize`Virgin Islands, US`, entity: 'xref-usa', zipPattern: /^008[0-9]{2}([ -][0-9]{4})?$/},
  {stripeCode: 'WK', name: $localize`Wake Island`, entity: 'xref-usa', zipPattern: /^96898$/},
  {stripeCode: 'YE', name: $localize`Yemen`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'ZM', name: $localize`Zambia`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'ZW', name: $localize`Zimbabwe`, entity: 'xref-usa', zipPattern: /.*/}
];

export interface StateOption {
  name: string;
  code: string;
}

export const COUNTRIES_WITH_STATE_DROPDOWN = ['US', 'CA'];

export const STATE_MAPPINGS: Record<string, StateOption[]> = {
  AU: [
    {name: 'Australian Capital Territory', code: 'ACT'},
    {name: 'New South Wales', code: 'NSW'},
    {name: 'Northern Territory', code: 'NT'},
    {name: 'Queensland', code: 'QLD'},
    {name: 'South Australia', code: 'SA'},
    {name: 'Tasmania', code: 'TAS'},
    {name: 'Victoria', code: 'VIC'},
    {name: 'Western Australia', code: 'WA'},
  ],
  US: [
    {name: 'Alabama', code: 'AL'},
    {name: 'Alaska', code: 'AK'},
    {name: 'Arizona', code: 'AZ'},
    {name: 'Arkansas', code: 'AR'},
    {name: 'California', code: 'CA'},
    {name: 'Colorado', code: 'CO'},
    {name: 'Connecticut', code: 'CT'},
    {name: 'Delaware', code: 'DE'},
    {name: 'District of Columbia', code: 'DC'},
    {name: 'Florida', code: 'FL'},
    {name: 'Georgia', code: 'GA'},
    {name: 'Hawaii', code: 'HI'},
    {name: 'Idaho', code: 'ID'},
    {name: 'Illinois', code: 'IL'},
    {name: 'Indiana', code: 'IN'},
    {name: 'Iowa', code: 'IA'},
    {name: 'Kansas', code: 'KS'},
    {name: 'Kentucky', code: 'KY'},
    {name: 'Louisiana', code: 'LA'},
    {name: 'Maine', code: 'ME'},
    {name: 'Maryland', code: 'MD'},
    {name: 'Massachusetts', code: 'MA'},
    {name: 'Michigan', code: 'MI'},
    {name: 'Minnesota', code: 'MN'},
    {name: 'Mississippi', code: 'MS'},
    {name: 'Missouri', code: 'MO'},
    {name: 'Montana', code: 'MT'},
    {name: 'Nebraska', code: 'NE'},
    {name: 'Nevada', code: 'NV'},
    {name: 'New Hampshire', code: 'NH'},
    {name: 'New Jersey', code: 'NJ'},
    {name: 'New Mexico', code: 'NM'},
    {name: 'New York', code: 'NY'},
    {name: 'North Carolina', code: 'NC'},
    {name: 'North Dakota', code: 'ND'},
    {name: 'Ohio', code: 'OH'},
    {name: 'Oklahoma', code: 'OK'},
    {name: 'Oregon', code: 'OR'},
    {name: 'Pennsylvania', code: 'PA'},
    {name: 'Rhode Island', code: 'RI'},
    {name: 'South Carolina', code: 'SC'},
    {name: 'South Dakota', code: 'SD'},
    {name: 'Tennessee', code: 'TN'},
    {name: 'Texas', code: 'TX'},
    {name: 'Utah', code: 'UT'},
    {name: 'Vermont', code: 'VT'},
    {name: 'Virginia', code: 'VA'},
    {name: 'Washington', code: 'WA'},
    {name: 'West Virginia', code: 'WV'},
    {name: 'Wisconsin', code: 'WI'},
    {name: 'Wyoming', code: 'WY'},
  ],
  CA: [
    {name: 'Alberta', code: 'AB'},
    {name: 'British Columbia', code: 'BC'},
    {name: 'Manitoba', code: 'MB'},
    {name: 'New Brunswick', code: 'NB'},
    {name: 'Newfoundland and Labrador', code: 'NL'},
    {name: 'Northwest Territories', code: 'NT'},
    {name: 'Nova Scotia', code: 'NS'},
    {name: 'Nunavut', code: 'NU'},
    {name: 'Ontario', code: 'ON'},
    {name: 'Prince Edward Island', code: 'PE'},
    {name: 'Quebec', code: 'QC'},
    {name: 'Saskatchewan', code: 'SK'},
    {name: 'Yukon', code: 'YT'},
  ],
  NZ: [
    {name: 'Auckland', code: 'AUK'},
    {name: 'Bay of Plenty', code: 'BOP'},
    {name: 'Canterbury', code: 'CAN'},
    {name: 'Chatham Islands Territory', code: 'CIT'},
    {name: 'Gisborne', code: 'GIS'},
    {name: 'Greater Wellington', code: 'WGN'},
    {name: "Hawke's Bay", code: 'HKB'},
    {name: 'Manawatu-Whanganui', code: 'MWT'},
    {name: 'Marlborough', code: 'MBH'},
    {name: 'Nelson', code: 'NSN'},
    {name: 'Northland', code: 'NTL'},
    {name: 'Otago', code: 'OTA'},
    {name: 'Southland', code: 'STL'},
    {name: 'Taranaki', code: 'TKI'},
    {name: 'Tasman', code: 'TAS'},
    {name: 'Waikato', code: 'WKO'},
    {name: 'West Coast', code: 'WTC'},
  ],
};

export const MODAL_COUNTRY_MAPPING: CountryOption[] = [
  {stripeCode: 'AU', name: $localize`Australia`, entity: 'xref-au', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'NZ', name: $localize`New Zealand`, entity: 'xref-nz', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'US', name: $localize`United States`, entity: 'xref-usa', zipPattern: /^\d{5}(?:[ -]\d{4})?$/},
  {stripeCode: 'CA', name: $localize`Canada`, entity: 'xref-can', zipPattern: /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i},
  {stripeCode: 'GB', name: $localize`United Kingdom`, entity: 'xref-uk', zipPattern: /^(?:GIR ?0AA|[A-Z]{1,2}\d[\dA-Z]? ?\d[ABD-HJLN-UW-Z]{2}|BFPO ?\d{1,4})$/i},
  {stripeCode: 'AE', name: $localize`United Arab Emirates`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'AQ', name: $localize`Antarctica`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'AG', name: $localize`Antigua and Barbuda`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'AF', name: $localize`Afghanistan`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AL', name: $localize`Albania`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AM', name: $localize`Armenia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AW', name: $localize`Aruba`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'AO', name: $localize`Angola`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AR', name: $localize`Argentina`, entity: 'xref-usa', zipPattern: /^(?:[A-HJ-NP-Z])?\d{4}(?:[A-Z]{3})?$/i},
  {stripeCode: 'AT', name: $localize`Austria`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AZ', name: $localize`Azerbaijan`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BA', name: $localize`Bosnia and Herzegovina`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'BD', name: $localize`Bangladesh`, entity: 'xref-au', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BE', name: $localize`Belgium`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BZ', name: $localize`Belize`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'BG', name: $localize`Bulgaria`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BF', name: $localize`Burkina Faso`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'BI', name: $localize`Burundi`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'BH', name: $localize`Bahrain`, entity: 'xref-uk', zipPattern: /^[0-9]{3,4}$/},
  {stripeCode: 'BJ', name: $localize`Benin`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'BM', name: $localize`Bermuda`, entity: 'xref-usa', zipPattern: /^[A-Z]{2} ?[A-Z0-9]{2}$/},
  {stripeCode: 'BN', name: $localize`Brunei`, entity: 'xref-au', zipPattern: /^[A-Z]{2} ?\d{4}$/i},
  {stripeCode: 'BO', name: $localize`Bolivia`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'BS', name: $localize`Bahamas`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'BT', name: $localize`Bhutan`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'BW', name: $localize`Botswana`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IO', name: $localize`British Indian Ocean Territory`, entity: 'xref-usa', zipPattern: /^BBND 1ZZ$/},
  {stripeCode: 'VG', name: $localize`British Virgin Islands`, entity: 'xref-usa', zipPattern: /^VG[0-9]{4}$/},
  {stripeCode: 'CH', name: $localize`Switzerland`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'CI', name: $localize`Ivory Coast`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'CM', name: $localize`Cameroon`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CF', name: $localize`Central African Republic`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'TD', name: $localize`Chad`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CL', name: $localize`Chile`, entity: 'xref-usa', zipPattern: /^[0-9]{7}$/},
  {stripeCode: 'CX', name: $localize`Christmas Island`, entity: 'xref-usa', zipPattern: /^6798$/},
  {stripeCode: 'CC', name: $localize`Cocos Islands`, entity: 'xref-usa', zipPattern: /^6799$/},
  {stripeCode: 'CO', name: $localize`Colombia`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'KM', name: $localize`Comoros`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CK', name: $localize`Cook Islands`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CR', name: $localize`Costa Rica`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'CY', name: $localize`Cyprus`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'CU', name: $localize`Cuba`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'CW', name: $localize`Curacao`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'CZ', name: $localize`Czech Republic`, entity: 'xref-uk', zipPattern: /^\d{3} ?\d{2}$/},
  {stripeCode: 'CD', name: $localize`Democratic Republic of the Congo`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'DE', name: $localize`Germany`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'DK', name: $localize`Denmark`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'DO', name: $localize`Dominican Republic`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'DZ', name: $localize`Algeria`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'TL', name: $localize`East Timor`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'EC', name: $localize`Ecuador`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'EE', name: $localize`Estonia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'EG', name: $localize`Egypt`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GQ', name: $localize`Equatorial Guinea`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'ER', name: $localize`Eritrea`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'ES', name: $localize`Spain`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'ET', name: $localize`Ethiopia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'FK', name: $localize`Falkland Islands`, entity: 'xref-usa', zipPattern: /^FIQQ 1ZZ$/},
  {stripeCode: 'FI', name: $localize`Finland`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'FJ', name: $localize`Fiji`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'FR', name: $localize`France`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GA', name: $localize`Gabon`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GH', name: $localize`Ghana`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GM', name: $localize`Gambia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GR', name: $localize`Greece`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GD', name: $localize`Grenada`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'GT', name: $localize`Guatemala`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'GG', name: $localize`Guernsey`, entity: 'xref-usa', zipPattern: /^GY\d[\dA-Z]? ?\d[ABD-HJLN-UW-Z]{2}$/},
  {stripeCode: 'GW', name: $localize`Guinea-Bissau`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'GY', name: $localize`Guyana`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'HK', name: $localize`Hong Kong`, entity: 'xref-au', zipPattern: /.*/},
  {stripeCode: 'HR', name: $localize`Croatia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'HU', name: $localize`Hungary`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'ID', name: $localize`Indonesia`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IR', name: $localize`Iran`, entity: 'xref-usa', zipPattern: /^[0-9]{5}-?[0-9]{5}$/},
  {stripeCode: 'IQ', name: $localize`Iraq`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IE', name: $localize`Ireland`, entity: 'xref-uk', zipPattern: /^[\dA-Z]{3} ?[\dA-Z]{4}$/i},
  {stripeCode: 'IM', name: $localize`Isle of Man`, entity: 'xref-usa', zipPattern: /^IM\d[\dA-Z]?\s?\d[ABD-HJLN-UW-Z]{2}$/},
  {stripeCode: 'IL', name: $localize`Israel`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IS', name: $localize`Iceland`, entity: 'xref-uk', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'IT', name: $localize`Italy`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'JM', name: $localize`Jamaica`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'JO', name: $localize`Jordan`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'JP', name: $localize`Japan`, entity: 'xref-au', zipPattern: /^\d{3}-?\d{4}$/},
  {stripeCode: 'JE', name: $localize`Jersey`, entity: 'xref-usa', zipPattern: /^JE\d[\dA-Z]?\s?\d[ABD-HJLN-UW-Z]{2}$/},
  {stripeCode: 'KE', name: $localize`Kenya`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'KH', name: $localize`Cambodia`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'KR', name: $localize`South Korea`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'KW', name: $localize`Kuwait`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'LA', name: $localize`Laos`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'LV', name: $localize`Latvia`, entity: 'xref-uk', zipPattern: /^LV-[0-9]{4}$/},
  {stripeCode: 'LC', name: $localize`Saint Lucia`, entity: 'xref-usa', zipPattern: /^LC\d{2} \d{3}$/},
  {stripeCode: 'LY', name: $localize`Libya`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'LI', name: $localize`Liechtenstein`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'LR', name: $localize`Liberia`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'LT', name: $localize`Lithuania`, entity: 'xref-uk', zipPattern: /^LT-[0-9]{5}$/},
  {stripeCode: 'LU', name: $localize`Luxembourg`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'MG', name: $localize`Madagascar`, entity: 'xref-uk', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'MK', name: $localize`North Macedonia`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'MY', name: $localize`Malaysia`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'ML', name: $localize`Mali`, entity: 'xref-uk', zipPattern: /.*/},
  {stripeCode: 'MX', name: $localize`Mexico`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'MD', name: $localize`Moldova`, entity: 'xref-usa', zipPattern: /^MD-?[0-9]{4}$/},
  {stripeCode: 'MC', name: $localize`Monaco`, entity: 'xref-uk', zipPattern: /^980[0-9]{2}$/},
  {stripeCode: 'MN', name: $localize`Mongolia`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'ME', name: $localize`Montenegro`, entity: 'xref-usa', zipPattern: /^8\d{4}$/},
  {stripeCode: 'MO', name: $localize`Macau`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'MT', name: $localize`Malta`, entity: 'xref-uk', zipPattern: /^[A-Z]{3} ?\d{2,4}$/i},
  {stripeCode: 'MR', name: $localize`Mauritania`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'MU', name: $localize`Mauritius`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'YT', name: $localize`Mayotte`, entity: 'xref-usa', zipPattern: /^976\d{2}$/},
  {stripeCode: 'MZ', name: $localize`Mozambique`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'MM', name: $localize`Myanmar`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'NA', name: $localize`Namibia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'NE', name: $localize`Niger`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'NG', name: $localize`Nigeria`, entity: 'xref-uk', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'NU', name: $localize`Niue`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'KP', name: $localize`North Korea`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'NL', name: $localize`Netherlands`, entity: 'xref-uk', zipPattern: /^[1-9]\d{3} ?(?:[A-RT-Z][A-Z]|S[BCE-RT-Z])$/i},
  {stripeCode: 'NO', name: $localize`Norway`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'OM', name: $localize`Oman`, entity: 'xref-uk', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'PA', name: $localize`Panama`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'PE', name: $localize`Peru`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'PH', name: $localize`Philippines`, entity: 'xref-au', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'PK', name: $localize`Pakistan`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'PL', name: $localize`Poland`, entity: 'xref-uk', zipPattern: /^[0-9]{2}-[0-9]{3}$/},
  {stripeCode: 'PN', name: $localize`Pitcairn Islands`, entity: 'xref-usa', zipPattern: /^PCRN 1ZZ$/},
  {stripeCode: 'PS', name: $localize`Palestine`, entity: 'xref-usa', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'PT', name: $localize`Portugal`, entity: 'xref-uk', zipPattern: /^[0-9]{4}-[0-9]{3}$/},
  {stripeCode: 'PY', name: $localize`Paraguay`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'QA', name: $localize`Qatar`, entity: 'xref-uk', zipPattern: /.*/},
  {stripeCode: 'RO', name: $localize`Romania`, entity: 'xref-uk', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'RS', name: $localize`Serbia`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'RW', name: $localize`Rwanda`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'BL', name: $localize`Saint Barthelemy`, entity: 'xref-usa', zipPattern: /^9[78][01]\d{2}$/},
  {stripeCode: 'SH', name: $localize`Saint Helena`, entity: 'xref-usa', zipPattern: /^(ASCN|STHL) 1ZZ$/},
  {stripeCode: 'KN', name: $localize`Saint Kitts and Nevis`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'PM', name: $localize`Saint Pierre and Miquelon`, entity: 'xref-usa', zipPattern: /^9[78]5\d{2}$/},
  {stripeCode: 'VC', name: $localize`Saint Vincent and the Grenadines`, entity: 'xref-usa', zipPattern: /^VC\d{4}$/},
  {stripeCode: 'WS', name: $localize`Samoa`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'ST', name: $localize`Sao Tome and Principe`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SA', name: $localize`Saudi Arabia`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'SC', name: $localize`Seychelles`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SL', name: $localize`Sierra Leone`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SE', name: $localize`Sweden`, entity: 'xref-uk', zipPattern: /^\d{3} ?\d{2}$/},
  {stripeCode: 'SG', name: $localize`Singapore`, entity: 'xref-au', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'SX', name: $localize`Sint Maarten`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SI', name: $localize`Slovenia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'SK', name: $localize`Slovakia`, entity: 'xref-uk', zipPattern: /^\d{3} ?\d{2}$/},
  {stripeCode: 'SB', name: $localize`Solomon Islands`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SO', name: $localize`Somalia`, entity: 'xref-usa', zipPattern: /^[A-Z]{2} ?\d{5}$/},
  {stripeCode: 'SM', name: $localize`San Marino`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'SN', name: $localize`Senegal`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'SS', name: $localize`South Sudan`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SD', name: $localize`Sudan`, entity: 'xref-usa', zipPattern: /^\d{5}$/},
  {stripeCode: 'SR', name: $localize`Suriname`,entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'SJ', name: $localize`Svalbard and Jan Mayen`, entity: 'xref-usa', zipPattern: /^\d{4}$/},
  {stripeCode: 'SV', name: $localize`El Salvador`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'TH', name: $localize`Thailand`, entity: 'xref-au', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'TG', name: $localize`Togo`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'TK', name: $localize`Tokelau`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'TN', name: $localize`Tunisia`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'TR', name: $localize`Turkey`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'TC', name: $localize`Turks and Caicos Islands`, entity: 'xref-usa', zipPattern: /^TKCA 1ZZ$/},
  {stripeCode: 'TT', name: $localize`Trinidad and Tobago`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'TW', name: $localize`Taiwan`, entity: 'xref-au', zipPattern: /^[0-9]{3,5}$/},
  {stripeCode: 'TZ', name: $localize`Tanzania`, entity: 'xref-uk', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'VI', name: $localize`U.S. Virgin Islands`, entity: 'xref-usa', zipPattern: /^008(?:[0-4]\d|5[01])(?:[ -]\d{4})?$/},
  {stripeCode: 'UY', name: $localize`Uruguay`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'UZ', name: $localize`Uzbekistan`, entity: 'xref-uk', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'VU', name: $localize`Vanuatu`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'VA', name: $localize`Vatican`, entity: 'xref-usa', zipPattern: /^00120$/},
  {stripeCode: 'VN', name: $localize`Vietnam`, entity: 'xref-au', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'WF', name: $localize`Wallis and Futuna`, entity: 'xref-usa', zipPattern: /^986\d{2}$/},
  {stripeCode: 'EH', name: $localize`Western Sahara`, entity: 'xref-usa', zipPattern: /^\d{5}$/},
  {stripeCode: 'ZA', name: $localize`South Africa`, entity: 'xref-uk', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'AS', name: $localize`American Samoa`, entity: 'xref-usa', zipPattern: /^96799$/},
  {stripeCode: 'AD', name: $localize`Andorra`, entity: 'xref-usa', zipPattern: /^AD[0-9]{3}$/},
  {stripeCode: 'AI', name: $localize`Anguilla`, entity: 'xref-usa', zipPattern: /^AI-2640$/},
  {stripeCode: 'BB', name: $localize`Barbados`, entity: 'xref-usa', zipPattern: /^BB[0-9]{5}$/},
  {stripeCode: 'BY', name: $localize`Belarus`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'BR', name: $localize`Brazil`, entity: 'xref-usa', zipPattern: /^[0-9]{5}-?[0-9]{3}$/},
  {stripeCode: 'IC', name: $localize`Canary Islands`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'CV', name: $localize`Cape Verde`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'KY', name: $localize`Cayman Islands`, entity: 'xref-usa', zipPattern: /^KY[1-3]-[0-9]{4}$/},
  {stripeCode: 'CN', name: $localize`China`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'DJ', name: $localize`Djibouti`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'DM', name: $localize`Dominica`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'FO', name: $localize`Faroe Islands`, entity: 'xref-usa', zipPattern: /^\d{3}$/},
  {stripeCode: 'GF', name: $localize`French Guiana`, entity: 'xref-usa', zipPattern: /^9[78]3[0-9]{2}$/},
  {stripeCode: 'PF', name: $localize`French Polynesia`, entity: 'xref-usa', zipPattern: /^987[0-9]{2}$/},
  {stripeCode: 'GE', name: $localize`Georgia`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'GI', name: $localize`Gibraltar`, entity: 'xref-usa', zipPattern: /^GX11 1AA$/},
  {stripeCode: 'GL', name: $localize`Greenland`, entity: 'xref-usa', zipPattern: /^39[0-9]{2}$/},
  {stripeCode: 'GP', name: $localize`Guadeloupe`, entity: 'xref-usa', zipPattern: /^9[78][01][0-9]{2}$/},
  {stripeCode: 'GU', name: $localize`Guam`, entity: 'xref-usa', zipPattern: /^969[123][0-9]([ -][0-9]{4})?$/},
  {stripeCode: 'GN', name: $localize`Guinea`, entity: 'xref-usa', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'HT', name: $localize`Haiti`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'HN', name: $localize`Honduras`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'IN', name: $localize`India`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'KI', name: $localize`Kiribati`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{6}$/},
  {stripeCode: 'KG', name: $localize`Kyrgyzstan`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'LB', name: $localize`Lebanon`, entity: 'xref-usa', zipPattern: /^[0-9]{4}( ?[0-9]{4})?$/},
  {stripeCode: 'LS', name: $localize`Lesotho`, entity: 'xref-usa', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'MW', name: $localize`Malawi`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'MV', name: $localize`Maldives`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'MH', name: $localize`Marshall Islands`, entity: 'xref-usa', zipPattern: /^969[67][0-9]([ -][0-9]{4})?$/},
  {stripeCode: 'MQ', name: $localize`Martinique`, entity: 'xref-usa', zipPattern: /^9[78]2[0-9]{2}$/},
  {stripeCode: 'FM', name: $localize`Micronesia`, entity: 'xref-usa', zipPattern: /^9694[1-4]([ -][0-9]{4})?$/},
  {stripeCode: 'MS', name: $localize`Montserrat`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{7}$/},
  {stripeCode: 'NP', name: $localize`Nepal`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'AN', name: $localize`Netherlands Antilles`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{6}/},
  {stripeCode: 'NC', name: $localize`New Caledonia`, entity: 'xref-usa', zipPattern: /^988[0-9]{2}$/},
  {stripeCode: 'NI', name: $localize`Nicaragua`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'NF', name: $localize`Norfolk Island`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'MP', name: $localize`Northern Mariana Islands`, entity: 'xref-usa', zipPattern: /^9695[012]([ -][0-9]{4})?$/},
  {stripeCode: 'PW', name: $localize`Palau`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'PG', name: $localize`Papua New Guinea`, entity: 'xref-usa', zipPattern: /^[0-9]{3}$/},
  {stripeCode: 'PR', name: $localize`Puerto Rico`, entity: 'xref-usa', zipPattern: /^00[679]\d{2}(?:[ -]\d{4})?$/},
  {stripeCode: 'RE', name: $localize`Reunion`, entity: 'xref-usa', zipPattern: /^9[78]4[0-9]{2}$/},
  {stripeCode: 'RU', name: $localize`Russia`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'MF', name: $localize`St. Martin`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'SZ', name: $localize`Swaziland`, entity: 'xref-usa', zipPattern: /^[A-Z0-9]{4}$/},
  {stripeCode: 'SY', name: $localize`Syria`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'TJ', name: $localize`Tajikistan`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'TO', name: $localize`Tonga`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'TM', name: $localize`Turkmenistan`, entity: 'xref-usa', zipPattern: /^[0-9]{6}$/},
  {stripeCode: 'TV', name: $localize`Tuvalu`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'UG', name: $localize`Uganda`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'UA', name: $localize`Ukraine`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'VE', name: $localize`Venezuela`, entity: 'xref-usa', zipPattern: /^[0-9]{4}$/},
  {stripeCode: 'WK', name: $localize`Wake Island`, entity: 'xref-usa', zipPattern: /^96898$/},
  {stripeCode: 'YE', name: $localize`Yemen`, entity: 'xref-usa', zipPattern: /.*/},
  {stripeCode: 'ZM', name: $localize`Zambia`, entity: 'xref-usa', zipPattern: /^[0-9]{5}$/},
  {stripeCode: 'ZW', name: $localize`Zimbabwe`, entity: 'xref-usa', zipPattern: /.*/}
];

